import { all } from "country-codes-list";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export const COUNTRY_CODES = all()
  .filter((c) => c.countryCallingCode)
  .sort((a, b) => a.countryNameEn.length - b.countryNameEn.length)
  .filter(
    (c, i, arr) =>
      arr.findIndex((x) => x.countryCallingCode === c.countryCallingCode) === i,
  )
  .map((c) => ({
    code: `+${c.countryCallingCode}`,
    flag: c.flag,
    label: c.countryCode,
    name: c.countryNameEn,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

export function isValidPhoneNumber(digits: string): boolean {
  return digits.length >= 6 && digits.length <= 15;
}

interface PhoneInputFieldProps {
  countryCode: string;
  phoneNumber: string;
  onCountryCodeChange: (code: string) => void;
  onPhoneNumberChange: (number: string) => void;
  placeholder?: string;
}

export function PhoneInputField({
  countryCode,
  phoneNumber,
  onCountryCodeChange,
  onPhoneNumberChange,
  placeholder = "Phone number",
}: PhoneInputFieldProps) {
  const [open, setOpen] = useState(false);
  const selected =
    COUNTRY_CODES.find((c) => c.code === countryCode) ?? COUNTRY_CODES[0];

  return (
    <div className="flex gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="auth.phone.country-trigger"
            className="flex items-center gap-1.5 bg-muted rounded-2xl px-3 py-3.5 text-base text-foreground shrink-0 outline-none focus:ring-2 focus:ring-primary"
          >
            <span>{selected.flag}</span>
            <span className="font-medium">{selected.code}</span>
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="start">
          <Command>
            <CommandInput
              placeholder="Search country..."
              className="text-base"
            />
            <CommandList className="max-h-60">
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {COUNTRY_CODES.map((c) => (
                  <CommandItem
                    key={c.label}
                    value={`${c.name} ${c.code}`}
                    onSelect={() => {
                      onCountryCodeChange(c.code);
                      setOpen(false);
                    }}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <span>{c.flag}</span>
                    <span className="flex-1 truncate text-sm">{c.name}</span>
                    <span className="text-muted-foreground text-xs shrink-0">
                      {c.code}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <input
        data-testid="auth.phone.input"
        type="tel"
        inputMode="numeric"
        value={phoneNumber}
        onChange={(e) => onPhoneNumberChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 bg-muted rounded-2xl px-4 py-3.5 text-base text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary"
      />
    </div>
  );
}
